import { useCallback, useEffect, useMemo, useState } from 'react'
import { useLocation } from 'react-router-dom'
import { EligibleProductStatus } from '@typesApp/Product'
import { useCreateClaimRecord } from '@hooks/create-claimrecord/useCreateClaimRecord'
import { useActiveData } from '@hooks/useActiveData'
import { useDebouncedCallback } from '@hooks/useDebouncedCallback'
import { useErrorPage } from '@hooks/useErrorPage'
import { usePublicInsurers } from '@hooks/usePublicInsurers'
import { useRestrictedCheck } from '@hooks/useRestrictedCheck'
import { useSessionInfo } from '@hooks/useSessionInfo'
import { logger } from '@utils/logger'
import { getLoginRedirectSearch, clearLoginRedirectSearch } from '@utils/navigationUtil'
import { useEligibleProductStatus } from './useEligibleProductStatus'
import { useFallbackHandler } from './useFallbackHandler'
import { useResumeFlow } from './useResumeFlow'

type UseInsuranceFormRedirectReturn = {
  isLoading?: boolean
}

export const useInsuranceFormRedirect = (): UseInsuranceFormRedirectReturn => {
  const location = useLocation()
  const [isLoading, setIsLoading] = useState(false)
  const { insurers } = usePublicInsurers()
  const { isAuthenticated, isSessionReady, isSessionInitializing, isGuestUser } = useSessionInfo()
  const { fetchActiveData } = useActiveData()
  const { createClaimRecord } = useCreateClaimRecord()
  const { fetchRestrictedStatus } = useRestrictedCheck()
  const { fetchEligibleProductStatus } = useEligibleProductStatus()
  const { handleRestricted, handleNoEligibleProducts } = useFallbackHandler()
  const { showErrorPage } = useErrorPage()
  const { resumeFlow } = useResumeFlow()

  // insurerId may come from navigation state or login redirect search parameter
  const insurerId = useMemo<string | undefined>(() => {
    let insurerId = location.state?.insurerId
    if (!insurerId) {
      const search = getLoginRedirectSearch()
      if (search) {
        const params = new URLSearchParams(search)
        insurerId = params.get('insurerId') ?? undefined
      }
    }

    // verify insurerId
    if (insurerId && !insurers.find(insurer => insurer.id === insurerId)) {
      insurerId = undefined
    }

    return insurerId
  }, [location.state?.insurerId, insurers])

  const handler = useCallback(async () => {
    try {
      setIsLoading(true)

      const restrictedStatus = await fetchRestrictedStatus()
      if (restrictedStatus?.restricted) {
        handleRestricted()
        return
      }

      const eligibleStatus = await fetchEligibleProductStatus()
      if (eligibleStatus !== EligibleProductStatus.ELIGIBLE) {
        handleNoEligibleProducts()
        return
      }

      const activeData = await fetchActiveData()
      let id = activeData?.id

      if (!id) {
        const result = await createClaimRecord({ insurerId })
        id = result.id
      }

      if (id) {
        await resumeFlow({ id, isNewClaimRecord: !activeData?.id })
      } else {
        showErrorPage()
      }
    } catch (error) {
      logger.warn(error)
      setIsLoading(false)
      showErrorPage()
    } finally {
      clearLoginRedirectSearch()
    }
  }, [
    showErrorPage,
    fetchActiveData,
    createClaimRecord,
    fetchRestrictedStatus,
    fetchEligibleProductStatus,
    handleRestricted,
    handleNoEligibleProducts,
    resumeFlow,
    insurerId,
  ])

  const debouncedHandler = useDebouncedCallback(handler, 50)

  const shouldProceed = (isAuthenticated || isGuestUser) && isSessionReady && !isLoading

  useEffect(() => {
    if (shouldProceed) {
      debouncedHandler()
      return () => debouncedHandler.cancel()
    }
  }, [debouncedHandler, shouldProceed])

  return {
    isLoading: isLoading || isSessionInitializing,
  }
}
